/**
 * Lymo Cart Savings
 *
 * This script handles the display of total cart savings based on:
 * 1. Items with compare-at prices
 * 2. Applied discount codes
 * 3. Updates when item quantities change
 */
(function () {
	// Configuration
	const config = {
		moneyFormat:
			window.themeVariables?.settings?.moneyFormat ||
			"{{amount_with_comma_separator}} €",
		debug: false, // Enable debugging
	};

	// Selectors
	const selectors = {
		cartSavingsSection: ".lymo-cart-savings",
		cartSavingsValue: ".lymo-cart-savings__value",
		cartTotal: "[data-cart-total]",
		cartForm: ".cart__form",
		quantitySelector: ".js-quantity-selector",
		cartDiscountSection: ".cart-discount",
		cartDiscountAmount: ".cart-discount__discount[data-role='cart']",
		productDiscountAmount: ".cart-discount__discount[data-role='product']",
		cartDiscountAutomated: ".cart-discount--automated",
		lineItemDiscount: ".cart-discount--automated[data-role='product']",
		cartLevelDiscount: ".cart-discount--automated[data-role='cart']",
		discountTag: ".cart-discount",
		discountValue: ".money",
		discountRow: ".cart-discount .row",
		cartItem: ".cart__item",
		cartItemPrice: ".cart__price",
		cartItemComparePrice: ".cart__price--compare",
		cartSubtotalSale: ".cart__subtotal-discounted.text-sale",
		cartSubtotalStrike: ".cart__subtotal-discounted.text-muted.text-strike",
	};

	// Store discount state
	let hasDiscountApplied = false;

	/**
	 * Debug logging function
	 */
	function debug(...args) {
		if (config.debug) {
			console.log("[Lymo Cart Savings]", ...args);
		}
	}

	/**
	 * Extract money amount from text
	 */
	function extractMoneyAmount(text) {
		if (!text) return 0;

		// Remove currency symbols and non-numeric characters except for decimal separator
		// Keep the minus sign if present
		let cleanedText = text.trim();
		const isNegative = cleanedText.includes("-");

		// Remove all non-numeric characters except decimal separator and thousands separator
		// First, remove the currency symbol and any other non-number characters
		cleanedText = cleanedText.replace(/[^\d,.'-]/g, "");

		// If the string has both commas and periods, determine which is the decimal separator
		// based on position (the last one is likely the decimal separator)
		if (cleanedText.includes(",") && cleanedText.includes(".")) {
			const lastCommaIndex = cleanedText.lastIndexOf(",");
			const lastDotIndex = cleanedText.lastIndexOf(".");

			// If the last separator is a comma, it's likely a decimal separator (European format)
			if (lastCommaIndex > lastDotIndex) {
				// Remove all dots (they were thousand separators)
				cleanedText = cleanedText.replace(/\./g, "");
				// Replace comma with dot for decimal separation
				cleanedText = cleanedText.replace(",", ".");
			} else {
				// It's likely US/UK format with period as decimal separator
				// Remove all commas (they were thousand separators)
				cleanedText = cleanedText.replace(/,/g, "");
			}
		} else if (cleanedText.includes(",")) {
			// Only commas present - determine if it's a decimal or thousands separator
			// If there's exactly one comma and it's followed by exactly 2 digits, it's likely a decimal
			const commaIndex = cleanedText.indexOf(",");
			if (commaIndex === cleanedText.length - 3) {
				// Replace comma with dot for decimal separation
				cleanedText = cleanedText.replace(",", ".");
			} else {
				// Remove all commas, they were likely thousand separators
				cleanedText = cleanedText.replace(/,/g, "");
			}
		}
		// If there are only dots, assume they're decimal separators and leave as is

		// Ensure we have a proper negative number if it was negative
		if (isNegative && !cleanedText.startsWith("-")) {
			cleanedText = "-" + cleanedText;
		}

		// Parse as float and convert to cents
		let amount = parseFloat(cleanedText) * 100;

		// If it's a negative amount, make it positive for our calculations
		if (amount < 0) {
			amount = Math.abs(amount);
		}

		// Debug the extraction process
		debug("Extracted money amount", {
			original: text,
			cleaned: cleanedText,
			amount: amount,
			formatted: theme.Currency.formatMoney(amount, config.moneyFormat),
		});

		return isNaN(amount) ? 0 : amount;
	}

	/**
	 * Calculate the true compare-at price by examining individual cart items
	 */
	function calculateTrueCompareAtPrice(cartContainer) {
		let trueCompareAtTotal = 0;

		// Find all cart items
		const cartItems = cartContainer.querySelectorAll(selectors.cartItem);
		debug(`Found ${cartItems.length} cart items`);

		cartItems.forEach((item, index) => {
			// Get the current price and compare-at price for this item
			const priceElement = item.querySelector(selectors.cartItemPrice);
			const compareElement = item.querySelector(selectors.cartItemComparePrice);

			// Get the quantity for this item
			// let quantity = 1;
			// const quantityInput = item.querySelector('input[name="updates[]"]');
			// if (quantityInput) {
			// 	quantity = parseInt(quantityInput.value, 10) || 1;
			// }

			// If there's a compare-at price, use it
			if (compareElement) {
				const moneyElement = compareElement.querySelector(".money");
				if (moneyElement) {
					const comparePrice = extractMoneyAmount(moneyElement.textContent);
					trueCompareAtTotal += comparePrice;
					debug(`Item #${index} has compare price: ${comparePrice} `);
				}
			}
			// Otherwise use the regular price
			else if (priceElement) {
				const moneyElement = priceElement.querySelector(".money");
				if (moneyElement) {
					const price = extractMoneyAmount(moneyElement.textContent);
					trueCompareAtTotal += price;
					debug(
						`Item #${index} has no compare price, using regular price: ${price}`
					);
				}
			}
		});

		return trueCompareAtTotal;
	}

	/**
	 * Update the subtotal display to show the strike price
	 */
	function updateSubtotalDisplay(
		cartTotalElement,
		currentTotalValue,
		currentTotalCompareValue
	) {
		if (!cartTotalElement) return;

		debug("Updating subtotal display", {
			currentTotalValue,
			currentTotalCompareValue,
			formattedTotal: theme.Currency.formatMoney(
				currentTotalValue,
				config.moneyFormat
			),
			formattedCompare: theme.Currency.formatMoney(
				currentTotalCompareValue,
				config.moneyFormat
			),
		});

		// Only update if there's a difference between the values
		if (currentTotalCompareValue > currentTotalValue) {
			const saleSpan = cartTotalElement.querySelector(
				selectors.cartSubtotalSale
			);
			const strikeSpan = cartTotalElement.querySelector(
				selectors.cartSubtotalStrike
			);

			debug("Found subtotal elements", { saleSpan, strikeSpan });

			if (saleSpan && strikeSpan) {
				// Update the sale price
				const saleMoneySpan = saleSpan.querySelector(".money");
				if (saleMoneySpan) {
					saleMoneySpan.innerHTML = theme.Currency.formatMoney(
						currentTotalValue,
						config.moneyFormat
					);
					debug("Updated sale price", {
						element: saleMoneySpan,
						value: currentTotalValue,
						formatted: theme.Currency.formatMoney(
							currentTotalValue,
							config.moneyFormat
						),
					});
				}

				// Update the strike price
				const strikeMoneySpan = strikeSpan.querySelector(".money");
				if (strikeMoneySpan) {
					strikeMoneySpan.innerHTML = theme.Currency.formatMoney(
						currentTotalCompareValue,
						config.moneyFormat
					);
					debug("Updated strike price", {
						element: strikeMoneySpan,
						value: currentTotalCompareValue,
						formatted: theme.Currency.formatMoney(
							currentTotalCompareValue,
							config.moneyFormat
						),
					});
				}

				// Make sure both elements are visible
				saleSpan.style.display = "inline-block";
				strikeSpan.style.display = "inline-block";
			} else {
				debug("Could not find subtotal elements to update, creating them");
				cartTotalElement.innerHTML = `<span class="cart__subtotal-discounted text-sale"><span class="money">${theme.Currency.formatMoney(
					currentTotalValue,
					config.moneyFormat
				)}</span></span>
        <span class="cart__subtotal-discounted text-muted text-strike"><span class="money">${theme.Currency.formatMoney(
					currentTotalCompareValue,
					config.moneyFormat
				)}</span></span>`;
			}
		} else {
			debug("No price difference to show in subtotal");
		}
	}

	/**
	 * Calculate and display the savings
	 */
	function updateCartSavings() {
		document
			.querySelectorAll(selectors.cartSavingsSection)
			.forEach((section) => {
				// Hide all sections by default
				section.style.display = "none";

				const savingsValueElement = section.querySelector(
					selectors.cartSavingsValue
				);
				const cartContainer =
					section.closest(".cart") || section.closest(".drawer");
				const cartTotalElement = cartContainer?.querySelector(
					selectors.cartTotal
				);

				if (!savingsValueElement || !cartContainer || !cartTotalElement) {
					debug("Missing required elements", {
						savingsValueElement,
						cartContainer,
						cartTotalElement,
					});
					return;
				}

				// Get the current total value
				const currentTotalValue = +cartTotalElement.dataset.currentTotalValue;

				// Calculate the true compare-at price by examining individual cart items
				const trueCompareAtPrice = calculateTrueCompareAtPrice(cartContainer);

				// Check for discount elements
				const discountElements = cartContainer.querySelectorAll(
					selectors.discountTag
				);

				// Determine if a discount is applied
				hasDiscountApplied = discountElements.length > 0;

				debug("Cart values", {
					currentTotalValue,
					trueCompareAtPrice,
					hasDiscountApplied,
					formattedTotal: theme.Currency.formatMoney(
						currentTotalValue,
						config.moneyFormat
					),
					formattedCompare: theme.Currency.formatMoney(
						trueCompareAtPrice,
						config.moneyFormat
					),
				});

				// Always update the subtotal display regardless of savings
				if (trueCompareAtPrice > currentTotalValue) {
					updateSubtotalDisplay(
						cartTotalElement,
						currentTotalValue,
						trueCompareAtPrice
					);
				}

				// Calculate savings from compare-at prices
				let totalSavings = 0;

				// Calculate savings only if both values are valid numbers
				if (
					Number.isFinite(currentTotalValue) &&
					Number.isFinite(trueCompareAtPrice) &&
					trueCompareAtPrice > 0 &&
					trueCompareAtPrice > currentTotalValue
				) {
					totalSavings = trueCompareAtPrice - currentTotalValue;
					debug("Total savings", {
						totalSavings,
						formatted: theme.Currency.formatMoney(
							totalSavings,
							config.moneyFormat
						),
					});
				}

				// Only show savings if there's an actual discount
				if (totalSavings > 0) {
					// Format the savings amount using the theme's currency formatter
					const formattedSavings = theme.Currency.formatMoney(
						totalSavings,
						config.moneyFormat
					);

					debug("Displaying savings:", formattedSavings);

					// Update the savings value and show the section
					savingsValueElement.removeAttribute("data-currency");
					savingsValueElement.innerHTML = formattedSavings;
					section.style.display = "block";
				} else {
					debug("No savings to display");
				}
			});

		// Trigger currency conversion if needed
		document.dispatchEvent(
			new CustomEvent("theme:addon:custom-currencies:convert-prices")
		);
	}

	/**
	 * Initialize the cart savings functionality
	 */
	function init() {
		debug("Initializing Lymo Cart Savings");

		// Add a class to the body to indicate the addon is enabled
		document.body.classList.add("lymo-cart-savings-enabled");

		// Hide all savings sections by default
		document
			.querySelectorAll(selectors.cartSavingsSection)
			.forEach((section) => {
				section.style.display = "none";
			});

		// Initialize discount code component if available
		if (theme.helpers && theme.helpers.loadComponent) {
			theme.helpers.loadComponent("discountCode", () => {
				if (theme.discountCode && theme.discountCode.init) {
					theme.discountCode.init();
					updateCartSavings();
				}
			});
		}

		// Listen for cart updates
		document.addEventListener("theme:cart:loaded", updateCartSavings);
		document.addEventListener("theme:cart:updated", updateCartSavings);

		// Listen for discount code updates
		document.addEventListener("theme:discount:updated", updateCartSavings);
		document.addEventListener("theme:discount:saved", updateCartSavings);
		document.addEventListener("theme:discount:removed", updateCartSavings);

		// Listen for quantity changes
		document.addEventListener("click", function (event) {
			const quantityButton = event.target.closest(".js-quantity-button");
			if (quantityButton) {
				// Use a small timeout to allow the cart to update first
				setTimeout(updateCartSavings, 150);
			}
		});

		// Initial update
		updateCartSavings();
	}

	// Initialize when the DOM is ready
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
